import { AbstractControl } from '@angular/forms';

import {
  AsyncValidatorOrOpts,
  CustomFormGroup,
  CustomFormGroupOptions,
  ValidatorOrOpts,
} from './custom-form-group';

/**
 * Utility type to convert a union of types into an intersection.
 * example: { a: string } | { b: number } => { a: string } & { b: number }
 */
type UnionToIntersection<U> = (U extends any ? (k: U) => void : never) extends (
  k: infer I
) => void
  ? I
  : never;

/**
 * Utility to extract the control type from a factory function.
 */
type ExtractControlFromFactory<F> = F extends (...args: any[]) => infer R
  ? R
  : AbstractControl<any, any, any>;

/**
 * Helper to get all individual conditions from a list of sets.
 */
type AllConditions<T> =
  T extends ReadonlyArray<{
    conditions: ReadonlyArray<infer O>;
  }>
    ? O
    : never;

/**
 * Extracts and merges controls from a single conditional control option.
 */
type ExtractControls<O> = O extends { key: infer K; factory: infer F }
  ? K extends string
    ? { [P in K]: ExtractControlFromFactory<F> }
    : never
  : never;

/**
 * Extracts the combined TConditional type from a list of ConditionalControlSet items.
 */
export type ExtractConditionalControlsFromSets<T> = UnionToIntersection<
  ExtractControls<AllConditions<T>>
>;

/**
 * Helper to infer the TConditional type from CustomFormGroupOptions
 */
type InferTConditional<T> = T extends { conditionalControls: infer Sets }
  ? Sets extends ReadonlyArray<any>
    ? ExtractConditionalControlsFromSets<Sets>
    : {}
  : {};

/**
 * Factory function to create a CustomFormGroup with enhanced type inference.
 *
 * This factory uses TypeScript's `const` type parameters (TS 5.0+) and function overloading
 * to provide a superior developer experience:
 *
 * 1. **Precise Type Inference**: When used with an `as const` options object, it accurately
 *    infers the keys and types of all conditional controls. For example, a conditional
 *    control with key 'asd' will be correctly typed as `AbstractControl | undefined`
 *    on the group's `controls` property.
 *
 * 2. **IDE Autocomplete**: Provides full autocomplete suggestions for the first-level
 *    options (like `errorMessages` and `conditionalControls`) when creating a new group.
 *
 * 3. **Type Safety**: Ensures that both static and conditional controls are correctly
 *    accounted for in the resulting `FormGroup` type, preventing "any" poisoning
 *    while still allowing for dynamic form structures.
 *
 * @param controls The static controls that are always present in the group.
 * @param options Configuration options including error messages and conditional control sets.
 *                Apply `as const` to the options object to enable precise key inference.
 * @param validatorOrOpts Sync validators or AbstractControlOptions.
 * @param asyncValidator Async validators.
 */
export function customFormGroup<
  TStatic extends { [K in keyof TStatic]: AbstractControl<any, any, any> },
  const TConstOptions extends CustomFormGroupOptions<any> | undefined =
    undefined,
>(
  controls: TStatic,
  options?: TConstOptions,
  validatorOrOpts?: ValidatorOrOpts,
  asyncValidator?: AsyncValidatorOrOpts
): CustomFormGroup<TStatic, InferTConditional<TConstOptions>>;

// eslint-disable-next-line no-redeclare
export function customFormGroup<
  TStatic extends { [K in keyof TStatic]: AbstractControl<any, any, any> },
>(
  controls: TStatic,
  options?: CustomFormGroupOptions<any>,
  validatorOrOpts?: ValidatorOrOpts,
  asyncValidator?: AsyncValidatorOrOpts
): CustomFormGroup<TStatic, any>;

// eslint-disable-next-line no-redeclare
export function customFormGroup<
  TStatic extends { [K in keyof TStatic]: AbstractControl<any, any, any> },
  TConstOptions extends CustomFormGroupOptions<any> | undefined = undefined,
>(
  controls: TStatic,
  options?: TConstOptions,
  validatorOrOpts?: ValidatorOrOpts,
  asyncValidator?: AsyncValidatorOrOpts
): CustomFormGroup<TStatic, any> {
  return new CustomFormGroup(
    controls,
    options,
    validatorOrOpts,
    asyncValidator
  );
}
